import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faCheck, faExclamationTriangle, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { doc, getDoc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { getFirestoreInstance } from '../../utils/firebaseHelper';
import { UserContext } from '../../context/UserContext';
import queryString from 'query-string';
import styles from './QRScanHandler.module.css';

// This component handles QR code scans and routes them appropriately
const QRScanHandler = () => {
  const { itemId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { userData } = useContext(UserContext);
  
  const [isLoading, setIsLoading] = useState(true);
  const [item, setItem] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [message, setMessage] = useState('');
  
  // Parse query parameters
  const queryParams = queryString.parse(location.search);
  const scanMode = queryParams.mode || 'view'; // Default to view mode
  const tourId = queryParams.tourId;
  const dateId = queryParams.dateId;
  
  useEffect(() => {
    if (!userData?.userId) {
      // Save the current URL for redirect after login
      sessionStorage.setItem('redirectAfterLogin', window.location.pathname + window.location.search);
      navigate('/login');
      return;
    }
    
    const handleScan = async () => {
      try {
        setIsLoading(true);
        
        // Find the item
        const db = getFirestoreInstance();
        
        // First try to find it by exact ID
        let itemDoc = await getDoc(doc(db, 'users', userData.userId, 'inventoryItems', itemId));
        
        // If not found, search for it across all users
        if (!itemDoc.exists()) {
          // Create a query to find the item in any user's inventory
          const allItemsQuery = query(
            collection(db, 'users', userData.userId, 'inventoryItems'),
            where('itemId', '==', itemId)
          );
          
          const querySnapshot = await getDocs(allItemsQuery);
          
          if (querySnapshot.empty) {
            setError('Item not found. Please check the QR code and try again.');
            setIsLoading(false);
            return;
          }
          
          // Use the first matching item
          itemDoc = querySnapshot.docs[0];
        }
        
        const itemData = itemDoc.data();
        setItem(itemData);
        
        // Check authorization
        const isOwner = itemData.vendorId === userData.userId;
        const isAllowed = itemData.allowedUserIds?.includes(userData.userId) || false;
        
        if (!isOwner && !isAllowed) {
          setError("You don't have permission to access this item.");
          setIsLoading(false);
          return;
        }
        
        // Handle different scan modes
        switch (scanMode) {
          case 'checklist':
            // Verify tour and date
            if (tourId && dateId) {
              try {
                // Check if this is a valid tour for the user
                const tourRef = doc(db, 'tours', tourId);
                const tourDoc = await getDoc(tourRef);
                
                if (!tourDoc.exists()) {
                  setError('Tour not found.');
                  break;
                }
                
                // Check if the user is part of this tour
                const tourData = tourDoc.data();
                if (!tourData.crewIds.includes(userData.userId) && tourData.ownerId !== userData.userId) {
                  setError('You are not authorized for this tour.');
                  break;
                }
                
                // Mark the item as checked
                const checklistRef = doc(db, 'tours', tourId, 'dates', dateId, 'checklist', itemData.itemId);
                await updateDoc(checklistRef, {
                  checked: true,
                  checkedBy: userData.userId,
                  checkedAt: new Date().toISOString()
                });
                
                setSuccess(true);
                setMessage(`${itemData.name} (${itemData.serialNumber}) checked successfully.`);
                
                // Automatically go back after a short delay
                setTimeout(() => {
                  window.history.back();
                }, 2000);
              } catch (err) {
                console.error('Error updating checklist:', err);
                setError('Failed to update checklist: ' + err.message);
              }
            } else {
              setError('Missing tour or date information for checklist.');
            }
            break;
            
          case 'view':
          default:
            // Navigate to the item details page
            const itemCategory = itemData.type.toLowerCase().replace(/\s+/g, '');
            const categoryMap = {
              'microphones': 'audioEquipment',
              'mixingconsoles': 'audioEquipment',
              'speakers': 'audioEquipment',
              'amplifiers': 'audioEquipment',
              'signalprocessors': 'audioEquipment',
              'directboxes(diboxes)': 'audioEquipment',
              'cablesandconnectors': 'audioEquipment',
              'standsandmounts': 'audioEquipment',
              'in-earmonitors(iem)': 'audioEquipment',
              'headphones': 'audioEquipment',
              'playbackandrecordingdevices': 'audioEquipment',
              'stageandstudioaccessories': 'audioEquipment',
              'wirelesssystems': 'audioEquipment',
              'powermanagement': 'audioEquipment',
              'monitoringandanalysis': 'audioEquipment',
              'miscellaneous': 'audioEquipment',
              // Add other category mappings as needed
            };
            
            const category = categoryMap[itemCategory] || 'audioEquipment';
            navigate(`/inventory/${category}/${itemData.itemId}`);
            return; // Return early since we're navigating away
        }
      } catch (error) {
        console.error('Error handling QR scan:', error);
        setError('An error occurred while processing the scan.');
      } finally {
        setIsLoading(false);
      }
    };
    
    handleScan();
  }, [itemId, userData, navigate, scanMode, tourId, dateId]);
  
  // Content to display based on state
  if (isLoading) {
    return (
      <div className={styles.scanContainer}>
        <div className={styles.loadingContainer}>
          <FontAwesomeIcon icon={faSpinner} spin className={styles.spinnerIcon} />
          <p>Processing QR code...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className={styles.scanContainer}>
        <div className={styles.errorContainer}>
          <FontAwesomeIcon icon={faExclamationTriangle} className={styles.errorIcon} />
          <h3>Scan Error</h3>
          <p>{error}</p>
          <button className={styles.backButton} onClick={() => navigate(-1)}>
            <FontAwesomeIcon icon={faArrowLeft} className={styles.buttonIcon} />
            Go Back
          </button>
        </div>
      </div>
    );
  }
  
  if (success) {
    return (
      <div className={styles.scanContainer}>
        <div className={styles.successContainer}>
          <FontAwesomeIcon icon={faCheck} className={styles.successIcon} />
          <h3>Success!</h3>
          <p>{message}</p>
        </div>
      </div>
    );
  }
  
  // If we reached here, view mode is active and we're redirecting
  return (
    <div className={styles.scanContainer}>
      <div className={styles.loadingContainer}>
        <FontAwesomeIcon icon={faSpinner} spin className={styles.spinnerIcon} />
        <p>Redirecting to item details...</p>
      </div>
    </div>
  );
};

export default QRScanHandler;